/**
 * Отправка попытки входа в вилку на сервер.
 *
 * Вкладка уже считает секунды между ногами и держит их в `localStorage` — по
 * ним лента раздаёт строкам пометку «может не дожить». Но этот замер умирает
 * вместе со вкладкой, и **сегодня нельзя ответить, сколько времени вход реально
 * занимает у людей**. Значит, нечем и доказать, что стало лучше.
 *
 * Сюда уезжает то же число плюс две добавки, без которых оно не значит ничего:
 * исход попытки (тридцать секунд с двумя ногами и тридцать с одной — успех и
 * убыток) и точность входа каждой ноги НА МОМЕНТ нажатия — именно её чинит
 * серверная адресация, и без записи сравнивать «до и после» будет не с чем.
 *
 * Причину отказа отсюда не спрашивают: интерфейс ленты не меняется, а гадать за
 * человека нельзя. Её принесёт расширение — оно одно знает, разлогинило ли его
 * контора и хватило ли на счету денег.
 *
 * Отправка беззвучная. Журнал — наш инструмент, а не его работа: отказ сети не
 * имеет права ни задержать ленту, ни показать ошибку.
 */

import { apiUrl } from './build';
import type { Fork, ForkLeg } from './protocol';

export type Outcome = 'both' | 'one' | 'none';

export interface Report {
  id: string;
  sport: string;
  line: string;
  roi: string;
  venues: string[];
  precisions: string[];
  outcome: Outcome;
  refusal?: string;
  /**
   * Код площадки, чья нога не встала. Именно код, а не номер ноги: номер
   * зависит от порядка, а порядок лента переставляет по риску отказа
   * (`next/order.ts`). Веса того порядка выведены из механизма отказа, и
   * проверить их можно только измеренной долей отказов по конторе.
   */
  failedVenue?: string;
  /** Миллисекунды от открытия первой конторы до подтверждения первой ноги. */
  firstMs?: number;
  /** ...и до подтверждения второй. */
  secondMs?: number;
  startedAt: string;
}

/** Точность входа ноги так, как её объявил сервер. Нет поля — считаем разделом. */
const precisionOf = (leg: ForkLeg): string => leg.p ?? (leg.direct === true ? 'event' : 'section');

export function reportFor(
  fork: Fork,
  outcome: Outcome,
  timing: {
    startedAt: number;
    firstAt?: number | null;
    secondAt?: number | null;
    failedVenue?: string | null;
  },
): Report {
  const legs = fork.legs ?? [];
  const body: Report = {
    id: fork.id,
    sport: fork.sport ?? '',
    line: fork.line ?? '',
    roi: fork.roi ?? '',
    venues: legs.map((leg) => leg.s),
    precisions: legs.map(precisionOf),
    outcome,
    startedAt: new Date(timing.startedAt).toISOString(),
  };
  if (timing.firstAt != null) body.firstMs = Math.max(0, timing.firstAt - timing.startedAt);
  if (timing.secondAt != null) body.secondMs = Math.max(0, timing.secondAt - timing.startedAt);
  // Только та площадка, что стоит в этой же вилке: чужой код испортил бы долю
  // отказов, посчитанную по конторе.
  if (timing.failedVenue != null && body.venues.includes(timing.failedVenue)) {
    body.failedVenue = timing.failedVenue;
  }
  return body;
}

export function report(body: Report): void {
  void fetch(apiUrl('/v1/attempt'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    // Попытка отмечается ровно в тот момент, когда человек закрывает вкладку
    // конторы и возвращается к ленте. Обычный fetch браузер в этот момент
    // отменяет вместе со страницей, keepalive — нет.
    keepalive: true,
  }).catch(() => {
    // Молча: журнал — наш инструмент, а не работа человека.
  });
}
